import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {Route} from 'react-router-dom';
import {useAuth0} from '@auth0/auth0-react';

import Config from 'hooks/config';
import redirectTo from 'utils/startup/redirectTo';
import SplashScreen from 'common/splash-screen/SplashScreen';
import DomainErrorBoundary from 'common/errors/DomainErrorBoundary';
import {
    isSandboxAuth0CallbackPath,
    rememberSandboxCallbackBasePath,
} from 'common/auth/sandboxCallbackCookie';

const {siteUrl} = Config;

const ProtectedRoute = ({component: Component, returnTo, ...args}) => {
    const {isLoading, isAuthenticated, loginWithRedirect, error} = useAuth0();

    useEffect(() => {
        if (error) {
            redirectTo(`${siteUrl}/auth/error?error=${error.error}`);

            return;
        }

        if (isLoading || isAuthenticated || isSandboxAuth0CallbackPath()) {
            return;
        }

        // using an IIFE here to immediately invoke the loginWithRedirect asynchronously
        (async () => {
            rememberSandboxCallbackBasePath();

            await loginWithRedirect({
                appState: {returnTo: returnTo || args?.location?.pathname},
            });
        })();
    }, [
        isLoading,
        isAuthenticated,
        loginWithRedirect,
        returnTo,
        args?.location?.pathname,
        error,
    ]);

    return isAuthenticated ? (
        <DomainErrorBoundary
            exact={args?.exact}
            pathname={args?.location?.pathname}
            routePath={args.path}
        >
            <Route render={Component} {...args} />
        </DomainErrorBoundary>
    ) : (
        <SplashScreen />
    );
};

ProtectedRoute.propTypes = {
    onRedirecting: PropTypes.element,
    component: PropTypes.func,
    returnTo: PropTypes.string,
};

export default ProtectedRoute;
